import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useMcpTokenMetadata, useRevokeMcpToken } from './useMcpToken';
import { GerarTokenModal } from './GerarTokenModal';

/**
 * MCP Connections Tab
 * Tab de "Conexões MCP" dentro de Configurações da Conta (/settings/mcp).
 * Gerencia o token MCP por-usuário (Claude Desktop, scripts, automações).
 */
export const McpTab: React.FC = () => {
  const { data: meta, isLoading } = useMcpTokenMetadata();
  const revoke = useRevokeMcpToken();
  const [modalOpen, setModalOpen] = useState(false);

  const formatDate = (date: string): string =>
    new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div
      className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8"
      data-testid="conexoes-mcp-page"
    >
      <div className="space-y-6">
        {/* Description */}
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Conecte ferramentas externas (Claude Desktop, scripts, automações) à sua conta via Model Context Protocol.
        </p>

        {/* Token Card */}
        <Card>
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="key" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Token de conexão
              </span>
            </div>
          </CardHeader>

          <CardContent className="px-4 pb-4">
            {isLoading && (
              <div className="flex items-center gap-2 py-4" data-testid="loading-state">
                <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  Carregando…
                </span>
              </div>
            )}

            {!isLoading && !meta?.exists && (
              <div className="flex flex-col items-center text-center py-8" data-testid="empty-state">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: 'var(--theme-input-background)' }}
                >
                  <Icon name="link" className="w-6 h-6" style={{ color: 'var(--theme-muted-text)' }} />
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
                  Nenhum token ativo
                </p>
                <p className="text-sm mb-4" style={{ color: 'var(--theme-muted-text)' }}>
                  Gere um token para conectar o Claude Desktop ou outras ferramentas MCP à sua conta.
                </p>
                <Button
                  icon="zap"
                  onClick={() => setModalOpen(true)}
                  data-testid="gerar-token-btn"
                >
                  Gerar token de conexão
                </Button>
              </div>
            )}

            {!isLoading && meta?.exists && (
              <div data-testid="token-info">
                <div
                  className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}
                >
                  <Icon name="check-circle" className="w-4 h-4" style={{ color: '#16a34a' }} />
                  <span className="text-sm font-medium" style={{ color: '#15803d' }}>
                    Token ativo
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                      Prefixo do token
                    </div>
                    <div
                      className="font-mono text-sm px-2 py-1 rounded-md inline-block"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        color: 'var(--theme-card-text)',
                        border: '1px solid var(--theme-card-border)',
                      }}
                      data-testid="token-prefix"
                    >
                      {meta.prefix}…
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                      Criado em
                    </div>
                    <div className="text-sm" style={{ color: 'var(--theme-card-text)' }} data-testid="token-created-at">
                      {meta.createdAt ? formatDate(meta.createdAt) : '-'}
                    </div>
                  </div>

                  {meta.lastUsedAt && (
                    <div>
                      <div className="text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Último uso
                      </div>
                      <div className="text-sm" style={{ color: 'var(--theme-card-text)' }} data-testid="token-last-used">
                        {formatDate(meta.lastUsedAt)}
                        {meta.lastUsedIp && (
                          <span className="ml-1" style={{ color: 'var(--theme-muted-text)' }}>
                            (IP {meta.lastUsedIp})
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {meta.expiresAt && (
                    <div>
                      <div className="text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Expira em
                      </div>
                      <div className="text-sm" style={{ color: 'var(--theme-card-text)' }} data-testid="token-expires-at">
                        {formatDate(meta.expiresAt)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
                  <Button
                    variant="secondary"
                    icon="refresh"
                    onClick={() => setModalOpen(true)}
                    data-testid="regenerar-btn"
                  >
                    Regenerar token
                  </Button>
                  <Button
                    variant="error"
                    icon="trash"
                    loading={revoke.isPending}
                    onClick={() => revoke.mutate()}
                    data-testid="revogar-btn"
                  >
                    Revogar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card>
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="help-circle" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Como usar
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ol className="space-y-3">
              {[
                'Gere um token de conexão acima.',
                'Copie o JSON de configuração gerado.',
                'No Claude Desktop, abra Configurações → Conexões MCP.',
                'Cole o JSON e salve. O TROIA aparecerá como ferramenta disponível.',
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>

      <GerarTokenModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        hasExisting={Boolean(meta?.exists)}
      />
    </div>
  );
};
